import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { formatTimestamp } from "../../../utils/utils";
import { formatMetric, metricQuality, qualityColor } from "../../../utils/road";

const FALLBACK_VIEW = { lat: -9.19, lng: -75.02, zoom: 5 };
const M_PER_DEG_LAT = 111320;

const deviceIcon = L.divIcon({
  className: "roadmap-device",
  html: '<div style="width:14px;height:14px;border-radius:50%;background:#1B365D;border:2px solid #fff;box-shadow:0 0 0 2px rgba(27,54,93,.35)"></div>',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

function cellBounds(cell) {
  const half = cell.cell_m / 2;
  const dLat = half / M_PER_DEG_LAT;
  const dLon = half / (M_PER_DEG_LAT * Math.cos((cell.lat * Math.PI) / 180));
  return [[cell.lat - dLat, cell.lon - dLon], [cell.lat + dLat, cell.lon + dLon]];
}

function escapeHtml(text) {
  return String(text ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

export default function RoadCells({ center, cells, metric, devices, changes, showChanges, impacts, showImpacts, focus }) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layers = useRef({});
  const fittedRef = useRef(false);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;
    const map = L.map(containerRef.current, { preferCanvas: true });
    L.tileLayer(process.env.REACT_APP_MAP_URL, { tileSize: 512, zoomOffset: -1 }).addTo(map);
    layers.current = {
      cells: L.layerGroup().addTo(map),
      changes: L.layerGroup().addTo(map),
      impacts: L.layerGroup().addTo(map),
      devices: L.layerGroup().addTo(map),
    };
    const start = center && center.latitude !== null && center.longitude !== null && center.zoom !== null
      ? { lat: center.latitude, lng: center.longitude, zoom: center.zoom }
      : FALLBACK_VIEW;
    map.setView([start.lat, start.lng], start.zoom);
    fittedRef.current = start !== FALLBACK_VIEW;
    mapRef.current = map;
    return () => {
      map.off();
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // El centro y zoom configurados en Empresa llegan con la respuesta, después
  // de crear el mapa: se aplican en cuanto están si todavía no se encuadró.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || fittedRef.current || !center) return;
    if (center.latitude === null || center.longitude === null || center.zoom === null
      || center.latitude === undefined || center.longitude === undefined || center.zoom === undefined) return;
    map.setView([center.latitude, center.longitude], center.zoom);
    fittedRef.current = true;
  }, [center]);

  useEffect(() => {
    const map = mapRef.current;
    const layer = layers.current.cells;
    if (!map || !layer) return;
    layer.clearLayers();
    const renderer = L.canvas({ padding: 0.5 });
    (cells || []).forEach((cell) => {
      const value = cell[metric];
      const q = metricQuality(metric, value);
      const rect = L.rectangle(cellBounds(cell), {
        renderer, stroke: true, color: "rgba(255,255,255,0.35)", weight: 0.5,
        fillColor: q === null ? "#9ca3af" : qualityColor(q * 25), fillOpacity: q === null ? 0.25 : 0.6, interactive: true,
      });
      rect.bindTooltip(`${t(`roadmap.metrics.${metric}`)}: <b>${escapeHtml(formatMetric(metric, value))}</b>`, { sticky: true, direction: "top", opacity: 0.95 });
      rect.on("click", () => {
        const rows = ["score", "rms_v", "rms_p95", "rms_h", "peak_g", "impacts", "speed_kmh"].map((k) => [k, formatMetric(k, cell[k])]);
        const html = `
          <h6>${escapeHtml(t("roadmap.cellPopup.title", { m: cell.cell_m }))}</h6>
          <div style="color:#6B7280;margin-bottom:6px;">${cell.n} ${escapeHtml(t("roadmap.cellPopup.windows"))} · ${cell.devices} ${escapeHtml(t("roadmap.cellPopup.devices"))}</div>
          <table style="border-collapse:collapse;">
            ${rows.map(([key, text]) => `<tr><td style="color:#6B7280;padding:1px 8px 1px 0;">${escapeHtml(t(`roadmap.metrics.${key}`))}</td><td style="font-weight:600;">${escapeHtml(text)}</td></tr>`).join("")}
          </table>
          <div style="color:#6B7280;margin-top:6px;">${escapeHtml(t("roadmap.cellPopup.last"))}: ${escapeHtml(formatTimestamp(cell.last_seen))}</div>`;
        L.popup({ maxWidth: 320 }).setLatLng([cell.lat, cell.lon]).setContent(html).openOn(map);
      });
      layer.addLayer(rect);
    });
    if (!fittedRef.current && cells && cells.length > 0) {
      map.fitBounds(L.latLngBounds(cells.map((c) => [c.lat, c.lon])).pad(0.2), { maxZoom: 16 });
      fittedRef.current = true;
    }
  }, [cells, metric, t]);

  useEffect(() => {
    const layer = layers.current.devices;
    if (!layer) return;
    layer.clearLayers();
    (devices || []).forEach((device) => {
      if (!device.position) return;
      const { latitude, longitude, created_at } = device.position;
      const marker = L.marker([latitude, longitude], { icon: deviceIcon });
      marker.bindTooltip(device.pdui_name, { permanent: true, direction: "top", offset: [0, -10], className: "netmap-device-tooltip" });
      marker.bindPopup(`
        <h6>${escapeHtml(device.pdui_name)}</h6>
        <div style="color:#6B7280;">${escapeHtml(t("roadmap.marker.last"))}: ${escapeHtml(formatTimestamp(created_at))}</div>
        <div style="margin-top:4px;">${escapeHtml(t("roadmap.metrics.score"))}: <b style="color:${qualityColor(device.score ?? 0)}">${device.score ?? "—"}</b> · ${escapeHtml(formatMetric("rms_v", device.rms_v))} · ${escapeHtml(formatMetric("speed_kmh", device.speed_kmh))}</div>`);
      layer.addLayer(marker);
    });
  }, [devices, t]);

  useEffect(() => {
    const layer = layers.current.changes;
    if (!layer) return;
    layer.clearLayers();
    if (!showChanges || !changes) return;
    [...(changes.worsened || []).map((c) => ({ ...c, kind: "worsened" })), ...(changes.improved || []).map((c) => ({ ...c, kind: "improved" }))].forEach((item) => {
      const icon = L.divIcon({
        className: "roadmap-change",
        html: `<div style="width:18px;height:18px;border-radius:50%;border:2px solid #fff;background:${item.kind === "worsened" ? "#DC2626" : "#16a34a"};color:#fff;font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center;">${item.kind === "worsened" ? "▼" : "▲"}</div>`,
        iconSize: [18, 18], iconAnchor: [9, 9],
      });
      const marker = L.marker([item.lat, item.lon], { icon });
      marker.bindTooltip(`${item.kind === "worsened" ? "▼" : "▲"} ${item.score_before} → ${item.score_after}`, { direction: "top", opacity: 0.95 });
      layer.addLayer(marker);
    });
  }, [changes, showChanges]);

  useEffect(() => {
    const layer = layers.current.impacts;
    if (!layer) return;
    layer.clearLayers();
    if (!showImpacts) return;
    (impacts || []).forEach((hit) => {
      if (hit.lat === null || hit.lat === undefined) return;
      const marker = L.circleMarker([hit.lat, hit.lon], { radius: 6, color: "#fff", weight: 1.5, fillColor: "#7c3aed", fillOpacity: 0.9 });
      marker.bindTooltip(`${escapeHtml(hit.pdui_name || "")}: ${formatMetric("peak_g", hit.peak_g)} · ${formatMetric("speed_kmh", hit.speed_kmh)} · ${escapeHtml(formatTimestamp(hit.created_at))}`, { direction: "top", opacity: 0.95 });
      layer.addLayer(marker);
    });
  }, [impacts, showImpacts]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focus) return;
    map.setView([focus.lat, focus.lon], Math.max(map.getZoom(), 16), { animate: true });
  }, [focus]);

  return <div ref={containerRef} style={{ width: "100%", height: "100%" }} />;
}
